import UIUtils from 'js/lib/UIUtils';
import { getEligibleSinkEntities } from 'js/reducers/selectors';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import has from 'lodash/has';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isAdminAccess, isManagedResource, isReadOnly } from 'js/lib/ACLUtils';
import { useMemo } from 'react';
import DateUtils from 'js/lib/DateUtils';
import { getStatusInfo } from 'new/hooks/adapters/statusInfo';

const getSourceMeta = (sinkId, state) => {
  const {
    entities: { sinks, vendors },
    paging: { metaInfoById },
  } = state;

  const sinkEntity = sinks[sinkId];
  let connectionOrVendorType = getConnectionOrVendorType(
    sinkEntity,
    vendors,
    'sink_type',
    'vendor_id',
  );
  return connectionOrVendorType && has(metaInfoById, connectionOrVendorType)
    ? metaInfoById[connectionOrVendorType]
    : null;
};

export function useMyDestinations() {
  const state = useSelector((state: any) => state);
  const {
    entities: { users, dataSets, vendors },
    sinks: { byId },
    request,
  } = state;
  const params = useParams();
  const sinkId = UIUtils.extractParamFromURL(params);
  const sinks = getEligibleSinkEntities(state);

  return useMemo(() => {
    const mappedSinks = Object.values(sinks).map((sink) => {
      const readOnly = isReadOnly(sink);
      const isManaged = isManagedResource(sink);
      const adminAccess = isAdminAccess(sink);
      const isWritingDataset = !!sink?.data_set_id;
      const writingDataSet =
        isWritingDataset && has(dataSets, sink.data_set_id) ? dataSets[sink.data_set_id] : null;

      const connectionOrVendorType = getConnectionOrVendorType(
        sink,
        vendors,
        'sink_type',
        'vendor_id',
      );

      const owner = has(sink, 'owner') && has(users, sink.owner) ? users[sink.owner] : null;

      const createdAt = DateUtils.convertDateStringToFormat(sink.created_at, 'MMM D, YYYY');
      const connectionInfo = getSourceMeta(sink.id, state);

      let sinkIcon;
      let sinkName;

      if (connectionInfo) {
        sinkIcon = connectionInfo.iconSm;
        sinkName = connectionInfo.name;
      } else {
        sinkName = connectionOrVendorType;
      }

      return {
        ...sink,
        icon: sinkIcon,
        sinkName,
        createdAt,
        readOnly,
        isManaged,
        adminAccess,
        owner,
        statusInfo: getStatusInfo(sink.status),
        connectionOrVendorType,
        isWritingDataset,
        writingDataSet,
      };
    });

    return {
      sinkId,
      sinks: mappedSinks,
      allSinks: byId,
      isLoading: request?.GET_SINKS_REQUEST || request?.GET_SINK_REQUEST,
    };
  }, [request, users, dataSets, vendors, sinks, byId, sinkId]);
}
